import classNames from "classnames";
import React, { memo, useState } from "react";
import { useSlaTeamMembers } from "../hooks/useSlaTeamMembers";
import { SlaActiveFilters } from "../types";

interface Props {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  filters: SlaActiveFilters;
  propertyId?: string;
}

const TeamMemberFilter = ({ selectedIds, onChange, filters, propertyId }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { members, isLoading } = useSlaTeamMembers(filters, propertyId);

  const toggle = (id: string) => {
    const isSelecting = !selectedIds.includes(id);
    const next = isSelecting
      ? [...selectedIds, id]
      : selectedIds.filter((x) => x !== id);
    onChange(next);
    // Close the dropdown on any selection change — selecting or deselecting.
    setIsOpen(false);
  };

  const label =
    selectedIds.length === 0
      ? "All team members"
      : selectedIds.length === 1
      ? (members.find((m) => m.id === selectedIds[0])?.firstname ?? "1 selected")
      : `${selectedIds.length} selected`;

  return (
    <div className="relative inline-block mr-3">
      <button
        type="button"
        className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-black-20 rounded text-13 hover:border-primary"
        style={{ width: 200 }}
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className="truncate text-left" style={{ minWidth: 0 }}>{label}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-black-20 rounded shadow-md z-50" style={{ width: 200 }}>
          <button
            type="button"
            className={classNames(
              "block w-full text-left px-4 py-2 text-13 hover:bg-black-5",
              { "font-semibold text-primary": selectedIds.length === 0 }
            )}
            onClick={() => {
              onChange([]);
              setIsOpen(false);
            }}
          >
            All team members
          </button>
          <div className="border-t border-black-10 max-h-48 overflow-y-auto">
            {isLoading && (
              <div className="px-4 py-2 text-12 text-black-40">Loading members…</div>
            )}
            {!isLoading && members.length === 0 && (
              <div className="px-4 py-2 text-12 text-black-40">
                No team members with active SLA tasks
              </div>
            )}
            {!isLoading && members.map((m) => (
              <label
                key={m.id}
                className="flex items-center gap-2 px-4 py-2 text-13 hover:bg-black-5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(m.id)}
                  onChange={() => toggle(m.id)}
                  className="mr-1"
                />
                {m.firstname} {m.surname}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(TeamMemberFilter);
